/**
 * Usage & Plan Card — Dashboard "fuel gauge" for tenant usage
 *
 * iPad Settings-inspired single-column layout with key-value rows,
 * inset resource limits, and clear cost footer.
 */

import { Link } from "react-router-dom";
import { Users, HardDrive, Store, Puzzle, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import {
  useGetTenantUsage,
  type TenantUsageResponse,
  type TenantUsageResource,
} from "@/hooks/useGetTenantUsage";
import { usePageTranslation } from "@/i18n/useTranslation";
import { dashboardTranslations } from "@/i18n/locales/en/dashboard";
import { dashboardTranslations as thDashboardTranslations } from "@/i18n/locales/th/dashboard";
import { formatTHB } from "@/lib/utils/formatCurrency";

// ── Resource row ──────────────────────────────────────────

function ResourceRow({ resource, isLast }: { resource: TenantUsageResource; isLast: boolean }) {
  const { label, count, limit } = resource;
  if (count === null) return null;
  const isOver = limit > 0 && count > limit;
  const pct = limit > 0 ? Math.min(Math.round((count / limit) * 100), 100) : 0;

  return (
    <>
      <div className="px-5 py-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm text-foreground">{label}</span>
          <span className={cn(
            "text-sm tabular-nums",
            isOver ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-muted-foreground"
          )}>
            {count} / {limit > 0 ? limit : "\u221E"}
          </span>
        </div>
        {limit > 0 && (
          <div className="h-1.5 w-full rounded-full bg-muted">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-300",
                isOver ? "bg-amber-500" : pct >= 80 ? "bg-amber-400" : "bg-primary/40"
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>
      {!isLast && <div className="mx-5 border-t border-border/40" />}
    </>
  );
}

// ── Skeleton ──────────────────────────────────────────────

function UsagePlanCardSkeleton() {
  return (
    <div className={cn(CARD, "p-0 shadow-none")}>
      <div className="px-5 pt-5 pb-4">
        <div className="h-5 w-24 animate-pulse rounded-md bg-muted" />
      </div>
      {[1, 2, 3, 4].map((i) => (
        <div key={i}>
          <div className="flex items-center justify-between px-5 py-4">
            <div className="h-4 w-28 animate-pulse rounded-md bg-muted" />
            <div className="h-4 w-16 animate-pulse rounded-md bg-muted" />
          </div>
          {i < 4 && <div className="mx-5 border-t border-border/40" />}
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────

export function UsagePlanCard() {
  const t = usePageTranslation({ en: dashboardTranslations, th: thDashboardTranslations });
  const { data, isFetched } = useGetTenantUsage();

  if (!isFetched) return <UsagePlanCardSkeleton />;

  const d = data as TenantUsageResponse;
  if (d.user_count === 0 && d.estimated_monthly_total_thb === 0 && !d.storage_metered_at) return null;

  const hasStorageOverage = d.storage_overage_gb > 0;
  const appNames = d.apps.map((a) => a.display_name).join(", ");

  const visibleResources = (d.resources ?? [])
    .filter((r) => r.count !== null && (r.limit > 0 || (r.count ?? 0) > 0))
    .sort((a, b) => {
      const aUtil = a.limit > 0 && a.count !== null ? a.count / a.limit : 0;
      const bUtil = b.limit > 0 && b.count !== null ? b.count / b.limit : 0;
      return bUtil - aUtil;
    });

  const topResources = visibleResources.slice(0, 4);
  const extraCount = visibleResources.length - 4;

  return (
    <div className={cn(CARD, "p-0 shadow-none")}>
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <span className="text-base font-semibold text-foreground">
          {d.plan_name ?? t.usagePlanTitle}
        </span>
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {t.usageThisMonth}
        </span>
      </div>

      {/* ── Key-value rows ── */}
      <div>
        {/* Users */}
        <div className="mx-5 border-t border-border/40" />
        <div className="flex items-center gap-3 px-5 py-4">
          <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-foreground flex-1">
            {d.user_count} {t.usageOfUnlimitedUsers}
          </span>
          <span className="text-sm font-medium tabular-nums text-foreground">
            {formatTHB(d.user_cost_thb)}
          </span>
        </div>

        {/* Storage */}
        <div className="mx-5 border-t border-border/40" />
        <div className="flex items-center gap-3 px-5 py-4">
          <HardDrive className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-foreground flex-1">
            {d.storage_bytes === 0
              ? t.usageNoFilesYet
              : `${d.storage_gb} / ${d.storage_included_gb} GB`}
          </span>
          <span className="text-xs text-muted-foreground">
            {hasStorageOverage
              ? formatTHB(d.storage_overage_cost_thb)
              : d.storage_bytes === 0
                ? `${d.storage_included_gb} GB`
                : t.usageIncluded}
          </span>
        </div>

        {/* Storefronts */}
        <div className="mx-5 border-t border-border/40" />
        <div className="flex items-center gap-3 px-5 py-4">
          <Store className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-foreground flex-1">
            {d.storefront_count} {d.storefront_count <= d.storefront_included
              ? t.usageStorefrontIncluded
              : t.usageStorefronts}
          </span>
          <span className="text-xs text-muted-foreground">
            {d.storefront_extra_count > 0 ? formatTHB(d.storefront_extra_cost_thb) : t.usageIncluded}
          </span>
        </div>

        {/* Apps */}
        <div className="mx-5 border-t border-border/40" />
        <div className="px-5 py-4">
          <div className="flex items-center gap-3">
            <Puzzle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm text-foreground flex-1">
              {d.active_app_count > 0
                ? `${d.active_app_count} ${t.usageAppsActive}`
                : t.usageNoAppsInstalled}
            </span>
          </div>
          {d.active_app_count > 0 && appNames && (
            <p className="text-xs text-muted-foreground mt-1.5 ml-7 leading-relaxed">{appNames}</p>
          )}
          {d.active_app_count === 0 && (
            <Link to="/dashboard" className="text-xs text-primary hover:underline mt-1.5 ml-7 block">
              {t.usageBrowseAppStore} →
            </Link>
          )}
        </div>
      </div>

      {/* ── Resource limits ── */}
      {topResources.length > 0 && (
        <>
          <div className="mx-5 border-t border-border/40" />
          <div className="px-5 pt-4 pb-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t.usageResourceLimits ?? "Resource Limits"}
            </p>
          </div>
          <div>
            {topResources.map((r, i) => (
              <ResourceRow key={r.key} resource={r} isLast={i === topResources.length - 1 && extraCount <= 0} />
            ))}
            {extraCount > 0 && (
              <>
                <div className="mx-5 border-t border-border/40" />
                <div className="px-5 py-3">
                  <Link to="/settings/usage" className="text-sm text-primary hover:underline">
                    +{extraCount} {t.usageResourceAndMore}
                  </Link>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ── Footer ── */}
      <div className="mx-5 border-t border-border/40" />
      <Link
        to="/settings/usage"
        className="flex items-center justify-between px-5 py-4 group transition-colors hover:bg-accent/50"
      >
        <div>
          <span className="text-xs text-muted-foreground">{t.usageEstimatedTotal}</span>
          <p className="text-base font-semibold tabular-nums text-foreground">
            {formatTHB(d.estimated_monthly_total_thb)}{t.usagePerMonth}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
      </Link>
    </div>
  );
}
